/**
 * App-level auth facade (M6 performance).
 *
 * Clerk (~105 KB brotli in our bundle + ~350 KB more from Clerk's CDN, about
 * 1.2 s of main-thread work on a mid-range phone) used to load on EVERY page,
 * although an anonymous visitor on a marketing page never needs it: without a
 * Clerk session cookie Clerk can only ever answer "signed out".
 *
 * Components therefore read auth through THIS context instead of Clerk's hooks:
 *   - Anonymous visitors (no `__client_uat` session hint, not on an auth
 *     screen) get {@link ANONYMOUS_AUTH}: loaded + signed out, instantly. Clerk
 *     never downloads.
 *   - Everyone else mounts the lazy Clerk runtime
 *     (`src/components/clerk-runtime.tsx`), which bridges Clerk's live state
 *     into this same context.
 *
 * The CLAUDE.md nav contract is untouched: CTAs default to "sign up" and only
 * switch to "Dashboard" once auth is loaded AND signed in (`showDashboardCta`).
 */
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

export type AppSignOut = (options?: { redirectUrl?: string }) => Promise<void>

export type AppAuth = {
  /** False only while a mounted Clerk runtime is still resolving the session. */
  isLoaded: boolean
  /** True only once Clerk confirmed an active session. */
  isSignedIn: boolean
  /** Ends the Clerk session (no-op for the anonymous runtime). */
  signOut: AppSignOut
  /** Whether the real Clerk runtime is mounted on this page. */
  clerkMounted: boolean
}

export const ANONYMOUS_AUTH: AppAuth = {
  isLoaded: true,
  isSignedIn: false,
  signOut: () => Promise.resolve(),
  clerkMounted: false,
}

const AppAuthContext = createContext<AppAuth>(ANONYMOUS_AUTH)

export function AppAuthProvider({
  value,
  children,
}: {
  value: AppAuth
  children: ReactNode
}) {
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
}

/** Drop-in for the `isLoaded` / `isSignedIn` / `signOut` part of Clerk's hooks. */
export function useAppAuth(): AppAuth {
  return useContext(AppAuthContext)
}
